/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines-per-function */
import React from "react";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import classNames from "classnames";
import { Tooltip } from "@mui/material";
import { formatPhone } from "utils/phone";
import { getLanguageNameByCode } from "../../utils";
import { useRequestBulkAssignOwner } from "../owner-bulk";
import SelectRow from "./select-row";
import SelectStage from "./select-stage";
import tableStyles from "./table.module.css";
import Star from "./star";
import OwnerCell from "./owner-cell";
import { useInterestsTable } from "./table-context";

const renderUnitSizes = (interest: any) => [
  interest.unitSizeSro ? "SRO" : null,
  interest.unitSizeStudio ? "Studio" : null,
  interest.unitSize1br ? "1BR" : null,
  interest.unitSize1brDen ? "1BR_DEN" : null,
  interest.unitSize2br ? "2BR" : null,
  interest.unitSize3br ? "3BR" : null,
  interest.unitSize4br ? "4BR" : null,
  interest.unitSize5br ? "5BR" : null,
  interest.unitSizeShared ? "Shared" : null,
]
  .filter(Boolean)
  .join(", ");

type Props = {
  interest: any;
  offset: number;
  hasStar: boolean;
  onStar: (bool: boolean) => void;
  slideoutEnabled: boolean;
  interestListObj: null | Record<string, string>;
  weightedPrefsEnabled: boolean;
  // Current user — powers the pinned "Me (Name)" option in the inline owner cell.
  currentUserId: string;
  currentUserName: string;
  // HAV-58/HAV-59 — the LIST the dashboard is currently filtering on (the table's
  // active interestListId: a concrete list id, or "all" for the multi-list
  // aggregate view). Threaded into the inline owner cell, which resolves it to its
  // property name (via the loaded assignableOwners) and floats that group to the
  // top of the picker. "all" keeps the picker alphabetical.
  activeInterestListId?: string | null;
  // M3-T02 — when false, the inline owner-cell editor affordance is hidden and
  // the owner renders read-only (the owner column itself still shows the name).
  // Defaults to true so callers that don't gate keep the M2 editable behavior.
  canAssignOwner?: boolean;
}

const TableRow: React.FC<Props> = ({
  interest,
  onStar,
  hasStar,
  offset,
  slideoutEnabled,
  interestListObj,
  weightedPrefsEnabled,
  currentUserId,
  currentUserName,
  activeInterestListId,
  canAssignOwner = true,
}) => {
  const { listId, p: pageParam } = useParams();
  const applicantName = (interest.applicantName || "").trim() || "N/A";
  const id = interest.id as string;
  // M4-T05 — the interest detail route, identical to the applicant-name links
  // below (slideout vs. legacy via `slideoutEnabled`). Passed to the owner cell so
  // the owner name links to the SAME detail target the applicant name does.
  let detailTo = `/dash/manager/interest/${id}/activity?offset=${offset}`;
  if (slideoutEnabled) {
    detailTo = `/dash/manager/lists/${listId}/p/${pageParam}/interests/${id}/activity`;
  }
  const { selectedInterestIds: ids, totalSelectedInterests } = useInterestsTable();
  const isRowhighlighted = window.location.href.match(id) || ids.includes(id);
  const { isBulkRow, requestBulkAssign } = useRequestBulkAssignOwner(slideoutEnabled);
  const bulkAssignLabel = `Assign owner to ${totalSelectedInterests} selected`;
  const bulkProps = isBulkRow(id) ? { onBulkAssign: requestBulkAssign, bulkAssignLabel } : {};

  return (
    <tr className={classNames({ [tableStyles.highlightedRow]: isRowhighlighted })}>
      <td>
        <SelectRow id={id} />
      </td>
      <td>{interest.positionNumber}</td>
      <td>
        <Star
          currentInterestID={id}
          hasStar={hasStar}
          onStar={onStar}
        />
      </td>
      <td className={tableStyles.content} style={{ maxWidth: "200px" }}>
        {interest.isDuplicate && (
          <span
            style={{
              background: "#2fb5c7",
              color: "white",
              padding: "1px 9px",
              fontSize: "12px",
              marginRight: "10px",
              marginLeft: "-2px",
            }}
          >
            Duplicate
          </span>
        )}

        <>
          {slideoutEnabled && (
            <Link
              className="open-slideout"
              style={{ color: "#2fb5c7", cursor: "pointer" }}
              to={`/dash/manager/lists/${listId}/p/${pageParam}/interests/${interest.id}/activity`}
            >
              {applicantName}
            </Link>
          )}

          {!slideoutEnabled && (
            <Link to={`/dash/manager/interest/${interest.id}/activity?offset=${offset}`}>
              {applicantName}
            </Link>)
          }

          {interest.prefLang && interest.prefLang !== "en" && (
            <span className="badge hc-badge">{getLanguageNameByCode(interest.prefLang)}</span>
          )}

          {interest.skips > 0 && (
            <Tooltip title="Applicant Skips">
              <span className={`badge ${tableStyles.skipContainer}`}>
                {interest.skips} {interest.skips > 1 ? "skips" : "skip"}
              </span>
            </Tooltip>
          )}
        </>
      </td>
      {interestListObj && (<td style={{ padding: "3px 4px" }}>
        {interestListObj[interest.interestListId]}
      </td>)}

      <td style={{ padding: "3px 4px" }}>
        <SelectStage interest={interest} />
      </td>
      <td className={tableStyles.content} style={{ maxWidth: "96px" }}>
        <a href={`tel:${formatPhone(interest.applicantPhone)}`}>
          {formatPhone(interest.applicantPhone)}
        </a>
      </td>
      <td style={{ textAlign: "center" }}>{interest.householdCount}</td>
      <td>{renderUnitSizes(interest)}</td>
      <td>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>${(interest.householdIncome || 0).toLocaleString()}</span>
          {(interest.householdReceivesOtherHousingAssistance ||
            interest.householdHasHousingAuthorityVoucher) && (
            <span className={`badge ${tableStyles.subsidyPill}`}>Subsidy</span>
          )}
        </div>
      </td>
      <td className={tableStyles.content} style={{ maxWidth: "160px" }}>
        {/* Keyed by interest so an in-flight owner change can't land on another row. */}
        <OwnerCell
          key={id}
          interestId={id}
          interestListId={interest.interestListId}
          ownerNames={interest.ownerNames}
          ownerUserIds={interest.ownerUserIds}
          applicantName={interest.applicantName ?? undefined}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
          activeInterestListId={activeInterestListId}
          canAssign={canAssignOwner}
          detailTo={detailTo}
          {...bulkProps}
        />
      </td>
      <td className={tableStyles.content} style={{ maxWidth: "96px", textAlign: "right" }}>
        {interest.submittedAt && moment(interest.submittedAt * 1000).format("MM/DD/YY h:mm A")}
        {weightedPrefsEnabled ? <span> || {interest.weight}</span> : ""}
      </td>
    </tr>
  );
};

export default TableRow;
